import React, {ReactElement} from 'react';
import {
    GeoJSONSource,
    LayerSpecification,
    Map,
    MapGeoJSONFeature,
    MapLayerMouseEvent,
    MapMouseEvent,
    NavigationControl,
    Point,
} from 'maplibre-gl';
import MaplibreGeocoder, {MaplibreGeocoderApi} from '@maplibre/maplibre-gl-geocoder';
import {FilterSpecification, LegacyFilterSpecification} from '@maplibre/maplibre-gl-style-spec';
import {filterByTrafficCenter} from '../../services/TrafficCenterService';
import {Log} from '../../utils/log';
import {Props} from './Props';
import {LayersState} from '../../scenes/MapScene/reducers/mapLayersReducer';
import {DataSource, DataSourceGeoJson, DataSourceVectorTiles} from '../../interfaces/DataSource';
import {css} from 'aphrodite/no-important';
import styles from './styles';
import {MapFeature} from '../../interfaces/MapFeature';
import {MapFlyTo} from '../../interfaces/MapFlyTo';
import {MapView} from '../../interfaces/Mapview';
import {HighlightFeatures, MapSettings} from '../../scenes/MapScene/reducers/generalReducer';
import {MapFilters, PrefixLayerFilter} from '../../scenes/MapScene/reducers/filterAndSearchReducer';
import {Feature, FeatureCollection, LineString, Point as GeoJSONPoint, Position} from 'geojson';
import {DataSourceState} from '../../scenes/MapScene/reducers/dataReducer';
import {findPlaces, findPlacesByGeocode} from '../../services/PlaceSearchService';
import {MapLayer} from '../../interfaces/MapLayer';
import {MAP_LAYER} from '../../interfaces/ActiveDialog';
import {
    startCachingMapsAccessToken,
    stopCachingMapsAccessToken,
    transformMapsRequest,
} from '../../services/MapsAuthenticationService';

const RESIZE_AFTER_MOUNT_MS = 200;
const REDRAW_DELAY_MS = 200;
const SET_GLOBAL_STATE_MS = 1000;

export default class MapGL extends React.Component<Props> {
    activeLayerIDs: string[];
    mapLayerToLayer: { [key: string]: MAP_LAYER };
    mapContainer: HTMLDivElement | null;
    mapHasFinishedLoading: boolean;
    mapGeoCoderContainer: HTMLDivElement | null;
    mapInstance: Map | null;
    mapNavigationControl: NavigationControl | null;
    mapLayerBaseFilters: { [key: string]: FilterSpecification };
    hoverTimeoutHolder?: NodeJS.Timeout;
    hoverTimeoutMS = 300;
    mapClickMargin = 15;
    numberOfFeaturesCalculationDelayMs = 2500;
    previousVisibleFeaturesCount = -1;

    constructor(props: Props) {
        super(props);

        this.activeLayerIDs = [];
        this.mapLayerToLayer = {};
        this.mapContainer = null;
        this.mapHasFinishedLoading = false;
        this.mapGeoCoderContainer = null;
        this.mapInstance = null;
        this.mapNavigationControl = null;
        this.mapLayerBaseFilters = {};
    }

    componentDidMount(): void {
        void this.initializeMap();
    }

    private readonly initializeMap = async (): Promise<void> => {
        const {styleUrl, viewport} = this.props;

        if (!this.mapContainer || !this.mapGeoCoderContainer) {
            return;
        }

        await startCachingMapsAccessToken();

        this.mapInstance = new Map({
            center: [viewport.longitude, viewport.latitude],
            container: this.mapContainer,
            style: styleUrl,
            transformRequest: transformMapsRequest,
            zoom: viewport.zoom,
        });

        const geocoderApi: MaplibreGeocoderApi = {
            forwardGeocode: findPlaces,
            reverseGeocode: findPlacesByGeocode,
        };

        const maplibreGeocoder = new MaplibreGeocoder(geocoderApi, {
            showResultsWhileTyping: true,
            reverseGeocode: false,
            marker: false,
            countries: 'nl',
            language: 'nl',
        });

        this.mapInstance?.addControl(new NavigationControl(), 'top-left');
        this.mapGeoCoderContainer.appendChild(maplibreGeocoder.onAdd(this.mapInstance));

        this.mapInstance.on('load', this.onMapLoad);
        this.mapInstance.on('moveend', this.onMapMoved);
        this.mapInstance.on('click', this.onMapClicked);
        this.mapInstance.on('contextmenu', this.onUserMapRightClicked);

        setTimeout(() => {
            if (this.mapInstance) {
                this.mapInstance.resize();
            }
        }, RESIZE_AFTER_MOUNT_MS);

        setInterval(() => {
            if(this.mapInstance){
                this.mapInstance.setGlobalStateProperty('nowMs', Date.now());
            }
        }, SET_GLOBAL_STATE_MS);

    };

    componentWillUnmount(): void {
        stopCachingMapsAccessToken();

        if (this.mapInstance) {
            this.mapInstance.off('load', this.onMapLoad);
            this.mapInstance.off('moveend', this.onMapMoved);
            this.mapInstance.off('click', this.onMapClicked);
            this.mapInstance.remove();
        }
    }

    componentDidUpdate(prevProps: Readonly<Props>): void {
        this.handleDataSourcesUpdate(this.props.dataSources, prevProps.dataSources, this.props.mapFilter);
        this.handleMapLayersUpdate(this.props.mapLayers, prevProps.mapLayers);
        this.handleStyleUrlUpdate(this.props.styleUrl, prevProps.styleUrl);
        this.handleFlyToLocationUpdate(this.props.mapFlyToLocation, prevProps.mapFlyToLocation);
        this.handleMapFilterUpdates(
            this.props.mapFilter,
            prevProps.mapFilter,
            this.props.showTrafficJamIcons,
            prevProps.showTrafficJamIcons,
            prevProps.dataSources
        );
        this.handleMapSettingsUpdate(this.props.mapSettings, prevProps.mapSettings);
        this.handleHighlightFeatures(this.props.highlightFeatures, prevProps.highlightFeatures);
        this.notifyNumberOfFeaturesVisibleOnMap();
    }

    render(): ReactElement {
        return (
            <>
                <div className={css(styles.mapContainer)} ref={(el) => (this.mapContainer = el)}/>
                <div className={css(styles.geoCoderWrapper)} ref={(el) => (this.mapGeoCoderContainer = el)}/>
            </>
        );
    }

    private readonly disableAllCurrentDataSourcesAndLayers = () => {
        const {mapLayers, dataSources} = this.props;

        for (const mapLayersKey in mapLayers) {
            if (!mapLayers.hasOwnProperty(mapLayersKey)) {
                continue;
            }

            const mapLayer = mapLayers[mapLayersKey];
            const dataSource = dataSources[mapLayer.source];

            if (mapLayer.isActive) {
                mapLayer.layers.forEach((layerConfiguration) =>
                    this.removeLayerFromMap(layerConfiguration, dataSource),
                );
            }
        }

        for (const dataSourceKey in dataSources) {
            if (!dataSources.hasOwnProperty(dataSourceKey)) {
                continue;
            }

            const dataSource = dataSources[dataSourceKey];

            if (dataSource.isActive) {
                this.removeSourceFromMap(dataSource);
            }
        }
    };

    private readonly enableAllCurrentDataSourcesAndLayers = () => {
        const {mapLayers, dataSources, mapFilter} = this.props;

        for (const dataSourceKey in dataSources) {
            if (!dataSources.hasOwnProperty(dataSourceKey)) {
                continue;
            }

            const dataSource = dataSources[dataSourceKey];

            if (dataSource.isActive) {
                this.addNewSourceToMap(dataSource);
                this.uploadSource(dataSource, mapFilter);
            }
        }

        for (const mapLayersKey in mapLayers) {
            if (!mapLayers.hasOwnProperty(mapLayersKey)) {
                continue;
            }

            const mapLayer = mapLayers[mapLayersKey];
            const dataSource = dataSources[mapLayer.source];

            if (mapLayer.isActive) {
                mapLayer.layers.forEach((layerConfiguration) => this.addNewLayerToMap(layerConfiguration, dataSource));
            }
        }
    };

    private readonly handleDataSourcesUpdate = (newDataSources: DataSourceState,
        oldDataSources: DataSourceState,
        filters: MapFilters,
        force: boolean = false) => {
        if (!this.mapInstance) {
            return;
        }

        for (const newDataSourceKey in newDataSources) {
            if (!newDataSources.hasOwnProperty(newDataSourceKey)) {
                continue;
            }

            const dataSource = newDataSources[newDataSourceKey];
            const oldDataSource = oldDataSources[newDataSourceKey];

            if (!dataSource && oldDataSource) {
                // The map source has been removed from the reducer, remove it from the map
                this.removeSourceFromMap(oldDataSource);
            } else if (dataSource.isActive && !oldDataSource.isActive) {
                // The dataSource new state is active, and it was not, add it to the map
                this.addNewSourceToMap(dataSource);
            } else if (!dataSource.isActive && oldDataSource && oldDataSource.isActive) {
                this.removeSourceFromMap(dataSource);
            } else if (oldDataSource && dataSource.sourceConfiguration !== oldDataSource.sourceConfiguration) {
                this.reEnableMaplayersAndDataSource(dataSource.id);
            } else if (
                force ||
                dataSource.isActive &&
                oldDataSource.isActive &&
                dataSource.currentIdentifier !== oldDataSource.currentIdentifier
            ) {
                // The dataSource has received a new data package
                this.uploadSource(dataSource, filters);
            }
        }
    };

    private readonly uploadSource = (dataSource: DataSource, filters: MapFilters) => {
        if (dataSource.dataLoadMethod === 'GeoJSON') {
            this.setDataGeoJsonSource(dataSource, filters);
        } else if (this.isVectorTilesSource(dataSource)) {
            this.setDataVectorTilesSource(dataSource);
        }
    };

    private readonly setDataGeoJsonSource = async (dataSource: DataSourceGeoJson, filters: MapFilters) => {
        const source = this.mapInstance?.getSource(dataSource.id) as unknown as GeoJSONSource | undefined;
        const {sourceConfiguration} = dataSource;
        if (source && sourceConfiguration.type === 'geojson') {
            const mapData = dataSource.layerFilteredMapData || dataSource.layerMapData;
            if (mapData) {
                const enrichedMapData = this.enrichedDataGeoJsonSource(dataSource, mapData);
                const filteredMapData = await filterByTrafficCenter(enrichedMapData, filters.currentTrafficCenter);
                source.setData(filteredMapData);
            } else if (
                sourceConfiguration.data &&
                typeof sourceConfiguration.data === 'object' &&
                sourceConfiguration.data.type === 'FeatureCollection'
            ) {
                source.setData(sourceConfiguration.data);
            }
        }
    };

    private readonly enrichedDataGeoJsonSource = (
        dataSource: DataSource,
        featureCollection: FeatureCollection,
    ): FeatureCollection => {
        switch (dataSource.id) {
            case 'fd':
                return this.featureCollectionWithPointsAddedForFdRecords(
                    featureCollection as FeatureCollection<LineString>,
                );
            case 'situation-otis':
                return this.featureCollectionWithIconPointsAddedForOtisRecords(featureCollection);
            default:
                return featureCollection;
        }
    };

    private readonly featureCollectionWithPointsAddedForFdRecords = (
        featureCollection: FeatureCollection<LineString>,
    ): FeatureCollection<LineString | GeoJSONPoint> => {
        return {
            ...featureCollection,
            features: this.lineStringsWithPoints(featureCollection.features, true),
        };
    };

    private readonly featureCollectionWithIconPointsAddedForOtisRecords = (
        featureCollection: FeatureCollection,
    ): FeatureCollection => {
        const otisFeatures = featureCollection.features.filter(
            (feature) => feature.properties?.dataType === 'situationTrafficMessageOtis',
        );
        const otherFeatures = featureCollection.features.filter(
            (feature) => feature.properties?.dataType !== 'situationTrafficMessageOtis',
        );

        const otisLineStringFeatures = otisFeatures.filter(
            (feature): feature is Feature<LineString> => feature.geometry.type === 'LineString',
        );
        const otisOtherGeometriesFeatures = otisFeatures.filter((feature) => feature.geometry.type !== 'LineString');

        return {
            ...featureCollection,
            features: [
                ...otherFeatures,
                ...otisOtherGeometriesFeatures,
                ...this.lineStringsWithPoints(otisLineStringFeatures, true),
            ],
        };
    };

    private readonly lineStringsWithPoints = (
        lineStringFeatures: Feature<LineString>[],
        useLastCoordinateAsPoint: boolean = false,
    ): Feature<LineString | GeoJSONPoint>[] => {
        return lineStringFeatures
            .map((feature) => {
                try {
                    const pointFeature = this.pointFromLineString(feature, useLastCoordinateAsPoint);
                    return [feature, pointFeature];
                } catch (error) {
                    Log.warn(error);
                    return [feature];
                }
            })
            .flatMap((lineAndPoint) => lineAndPoint);
    };

    private readonly pointFromLineString = (
        lineStringFeature: Feature<LineString>,
        useLastCoordinateAsPoint: boolean,
    ): Feature<GeoJSONPoint> => {
        if (lineStringFeature.geometry && 'coordinates' in lineStringFeature.geometry) {
            const coordinatesToUseForPoint: Position = useLastCoordinateAsPoint
                ? lineStringFeature.geometry.coordinates[lineStringFeature.geometry.coordinates.length - 1]
                : lineStringFeature.geometry.coordinates[0];
            return {
                ...lineStringFeature,
                geometry: {
                    type: 'Point',
                    coordinates: coordinatesToUseForPoint,
                },
                id: `${lineStringFeature.id}`,
                properties: {
                    ...lineStringFeature.properties,
                    id: `${lineStringFeature.id}`,
                },
            };
        } else {
            throw new Error(
                `No coordinates could be found on LineStringFeature: ${lineStringFeature.id}, geometry: ${lineStringFeature.geometry}`,
            );
        }
    };

    private readonly setDataVectorTilesSource = (dataSource: DataSourceVectorTiles) => {
        if (!this.mapInstance) {
            return;
        }

        if (!dataSource.sourceLayer.length || !dataSource.layerMapData) {
            return;
        }

        // Clear the current feature state
        this.mapInstance.removeFeatureState({
            source: dataSource.id,
            sourceLayer: dataSource.sourceLayer,
        });

        const mapFeatureStateData = dataSource.layerFilteredMapData || dataSource.layerMapData;

        Object.keys(mapFeatureStateData).forEach((index) => {
            const feature = mapFeatureStateData[index];
            if (!this.mapInstance) {
                return;
            }

            this.mapInstance.setFeatureState(
                {
                    id: index,
                    source: dataSource.id,
                    sourceLayer: dataSource.sourceLayer,
                },
                feature as never,
            );
        });
    };

    private readonly handleMapLayersUpdate = (newMapLayers: LayersState, oldMapLayers: LayersState) => {
        if (!this.mapInstance) {
            return;
        }

        for (const newMapLayersKey in newMapLayers) {
            if (!newMapLayers.hasOwnProperty(newMapLayersKey)) {
                continue;
            }

            const mapLayer = newMapLayers[newMapLayersKey];
            const oldMapLayer: MapLayer = oldMapLayers[newMapLayersKey];
            const dataSource = this.props.dataSources[mapLayer.source];

            this.handleMapLayerUpdate(mapLayer, oldMapLayer, dataSource);
        }
    };

    private handleMapLayerUpdate(mapLayer: MapLayer, oldMapLayer: MapLayer, dataSource: DataSource) {
        if (mapLayer && !oldMapLayer) {
            for (const layer of mapLayer.layers) {
                this.mapLayerToLayer[layer.id] = mapLayer.id as unknown as MAP_LAYER;
            }
        } else if (!mapLayer && oldMapLayer) {
            for (const layer of oldMapLayer.layers) {
                delete this.mapLayerToLayer[layer.id];
            }
        }

        if (this.isMapLayerActivated(mapLayer, oldMapLayer)) {
            mapLayer.layers.forEach((layer) => {
                this.addNewLayerToMap(layer, dataSource);
            });
            this.setFilters(this.props.mapFilter, this.props.showTrafficJamIcons);
        } else if (this.isMapLayerDeactivated(mapLayer, oldMapLayer)) {
            mapLayer.layers.forEach((layer) => {
                this.removeLayerFromMap(layer, dataSource);
            });
        }
    }

    private isMapLayerActivated(mapLayer: MapLayer, oldMapLayer: MapLayer) {
        return mapLayer.isActive && !oldMapLayer.isActive;
    }

    private isMapLayerDeactivated(mapLayer: MapLayer, oldMapLayer: MapLayer) {
        return !mapLayer.isActive && oldMapLayer && oldMapLayer.isActive;
    }

    private readonly handleStyleUrlUpdate = (currentUrl: string, previousUrl: string) => {
        if (!this.mapInstance) {
            return;
        }
        if (!this.mapHasFinishedLoading) {
            return;
        }

        if (currentUrl !== previousUrl) {
            this.disableAllCurrentDataSourcesAndLayers();

            this.mapInstance.setStyle(currentUrl);

            setTimeout(() => {
                this.enableAllCurrentDataSourcesAndLayers();
                this.setFilters(this.props.mapFilter, this.props.showTrafficJamIcons);
            }, REDRAW_DELAY_MS);
        }
    };

    private readonly handleFlyToLocationUpdate = (
        currentFlyToLocation: MapFlyTo | null,
        previousMapFlyToLocation: MapFlyTo | null,
    ) => {
        if (!currentFlyToLocation) {
            return;
        }

        if (!previousMapFlyToLocation) {
            this.mapFlyToLocation(currentFlyToLocation);
            return;
        }

        if (
            currentFlyToLocation.center[0] !== previousMapFlyToLocation.center[0] ||
            currentFlyToLocation.center[1] !== previousMapFlyToLocation.center[1] ||
            currentFlyToLocation.offset[0] !== previousMapFlyToLocation.offset[0] ||
            currentFlyToLocation.offset[1] !== previousMapFlyToLocation.offset[1] ||
            currentFlyToLocation.zoom !== previousMapFlyToLocation.zoom
        ) {
            this.mapFlyToLocation(currentFlyToLocation);
        }
    };

    private readonly handleMapFilterUpdates = (
        currentMapFilters: MapFilters,
        previousMapFilters: MapFilters | null,
        showTrafficJamIcons: boolean,
        prevShowTrafficJamIcons: boolean,
        prevDataSources: DataSourceState
    ) => {
        if (
            !previousMapFilters ||
            currentMapFilters.filterTimeStamp !== previousMapFilters.filterTimeStamp ||
            showTrafficJamIcons !== prevShowTrafficJamIcons
        ) {
            this.setFilters(currentMapFilters, showTrafficJamIcons);
            this.handleDataSourcesUpdate(this.props.dataSources, prevDataSources, this.props.mapFilter, true);
        }
    };

    private readonly handleMapSettingsUpdate = (currentMapSettings: MapSettings, previousMapSettings: MapSettings) => {
        if (currentMapSettings.identifier !== previousMapSettings.identifier) {
            this.setLineOffsetTravelTimeLayerIfZoomLevelReached(currentMapSettings.travelTimeFcdOffset);
        }
    };

    private readonly handleHighlightFeatures = (
        currentHighlightFeatures: HighlightFeatures | null,
        previousHighlightFeatures: HighlightFeatures | null,
    ) => {
        if (!!currentHighlightFeatures && !previousHighlightFeatures) {
            this.setHighlightSourceFeatures(currentHighlightFeatures.features);
        } else if (
            !!currentHighlightFeatures &&
            !!previousHighlightFeatures &&
            currentHighlightFeatures.identifier !== previousHighlightFeatures.identifier
        ) {
            this.setHighlightSourceFeatures(currentHighlightFeatures.features);
        } else if (!currentHighlightFeatures && !!previousHighlightFeatures) {
            this.setHighlightSourceFeatures([]);
        }
    };

    private readonly onMapLoad = () => {
        this.props.mapHasFinishedLoading();
        this.mapHasFinishedLoading = true;

        this.setupHighLightLayer();
    };

    private readonly onMapMoved = () => {
        if (!this.mapInstance) {
            return;
        }

        const {lat, lng} = this.mapInstance.getCenter();
        const zoom = this.mapInstance.getZoom();

        const viewport: MapView = {
            latitude: lat,
            longitude: lng,
            zoom: zoom,
        };

        this.props.updateMapPosition(viewport);
    };

    private readonly onMouseEnter = (e: MapLayerMouseEvent, dataSource: DataSource) => {
        if (!this.mapInstance) {
            return;
        }

        let enablePointer = false;

        if (this.isVectorTilesSource(dataSource)) {
            if (e.features) {
                e.features.forEach((feature) => {
                    if (feature.state && feature.state.color) {
                        enablePointer = true;
                    }
                });
            }
        } else {
            enablePointer = true;
        }

        if (enablePointer) {
            this.mapInstance.getCanvas().style.cursor = 'pointer';
            this.hoverTimeoutHolder = setTimeout(
                () => {
                    if(!e.features){
                        return;
                    }
                    this.props.onUserHoverOverFeature(
                        e.features.map((feature) => this.toMapClickedFeature(feature)),
                        e.point.x,
                        e.point.y,
                    );
                },
                this.hoverTimeoutMS,
            );
        }
    };

    private readonly onMouseLeave = () => {
        if (!this.mapInstance) {
            return;
        }

        this.mapInstance.getCanvas().style.cursor = '';
        this.props.onUserExistsHoverOverFeature();
        if (this.hoverTimeoutHolder) {
            clearTimeout(this.hoverTimeoutHolder);
        }
    };

    private readonly addNewSourceToMap = (dataSource: DataSource) => {
        if (!this.mapInstance) {
            return;
        }

        this.mapInstance.addSource(dataSource.id, dataSource.sourceConfiguration);
    };

    private readonly addNewLayerToMap = (layerConfiguration: LayerSpecification, dataSource: DataSource) => {
        if (!this.mapInstance) {
            return;
        }
        const {mapSettings} = this.props;

        this.mapInstance.addLayer(layerConfiguration);
        this.mapInstance.on('mouseenter', layerConfiguration.id, (e: MapLayerMouseEvent) =>
            this.onMouseEnter(e, dataSource),
        );
        this.mapInstance.on('mouseleave', layerConfiguration.id, this.onMouseLeave);

        this.activeLayerIDs = [...this.activeLayerIDs, layerConfiguration.id];

        if (layerConfiguration.type !== 'background' && layerConfiguration.filter) {
            this.mapLayerBaseFilters[layerConfiguration.id] = layerConfiguration.filter;
        }

        if (
            (layerConfiguration.id === 'travelTimesFcdLine' || layerConfiguration.id === 'travelTimesOther') &&
            mapSettings.travelTimeFcdOffset > 0
        ) {
            this.setLineOffsetTravelTimeLayerIfZoomLevelReached(mapSettings.travelTimeFcdOffset);
        }

        this.updateLayerSequence();
    };

    private readonly removeSourceFromMap = (dataSource: DataSource) => {
        if (!this.mapInstance) {
            return;
        }
        if (!this.mapInstance.getSource(dataSource.id)) {
            return;
        }

        const {mapLayers} = this.props;

        for (const mapLayersKey in mapLayers) {
            if (!mapLayers.hasOwnProperty(mapLayersKey)) {
                continue;
            }

            const item = mapLayers[mapLayersKey];

            if (item.source === dataSource.id && item.isActive) {
                item.layers.forEach((layer: LayerSpecification) => this.removeLayerFromMap(layer, dataSource));
            }
        }

        this.mapInstance.removeSource(dataSource.id);
    };

    private readonly reEnableMaplayersAndDataSource = (dataSourceKey: string) => {
        const {mapLayers, dataSources, mapFilter} = this.props;

        if (!dataSources.hasOwnProperty(dataSourceKey)) {
            return;
        }

        const dataSource = dataSources[dataSourceKey];
        this.removeSourceFromMap(dataSource);

        if (dataSource.isActive) {
            this.addNewSourceToMap(dataSource);
            this.uploadSource(dataSource, mapFilter);
        }

        for (const mapLayersKey in mapLayers) {
            if (!mapLayers.hasOwnProperty(mapLayersKey)) {
                continue;
            }

            const mapLayer = mapLayers[mapLayersKey];
            const dataSourceOfLayer = dataSources[mapLayer.source];

            if (dataSourceOfLayer.id !== dataSourceKey) {
                continue;
            }

            if (mapLayer.isActive) {
                mapLayer.layers.forEach((layerConfiguration) => this.addNewLayerToMap(layerConfiguration, dataSourceOfLayer));
            }
        }
    };

    private readonly removeLayerFromMap = (layerConfiguration: LayerSpecification, dataSource: DataSource) => {
        if (!this.mapInstance) {
            return;
        }

        if (!this.mapInstance.getLayer(layerConfiguration.id)) {
            return;
        }

        this.mapInstance.off('mouseenter', layerConfiguration.id, (e: MapLayerMouseEvent) =>
            this.onMouseEnter(e, dataSource),
        );
        this.mapInstance.off('mouseleave', layerConfiguration.id, this.onMouseLeave);
        this.mapInstance.removeLayer(layerConfiguration.id);

        this.activeLayerIDs = this.activeLayerIDs.filter((item: string) => item !== layerConfiguration.id);

        if (this.mapLayerBaseFilters[layerConfiguration.id]) {
            delete this.mapLayerBaseFilters[layerConfiguration.id];
        }

        this.updateLayerSequence();
    };

    private readonly onMapClicked = (e: MapMouseEvent) => {
        if (!this.mapInstance) {
            return;
        }

        const foundFeatures: { [key: string]: MapFeature } = {};
        const leftUpPoint = new Point(e.point.x - this.mapClickMargin, e.point.y - this.mapClickMargin);
        const rightDownPoint = new Point(e.point.x + this.mapClickMargin, e.point.y + this.mapClickMargin);
        const box: [Point, Point] = [leftUpPoint, rightDownPoint];

        const queriedFeatures = this.mapInstance.queryRenderedFeatures(box, {
            layers: this.activeLayerIDs,
        });

        queriedFeatures.filter((feature: MapGeoJSONFeature) =>
            !(this.isVectorTilesSource(this.props.dataSources[feature.source])
                && Object.keys(feature.state).length === 0))
            .forEach((feature: MapGeoJSONFeature) => {
                const id = feature.id ? feature.id : feature.properties.id;
                foundFeatures[id] = this.toMapClickedFeature(feature);
            });

        this.props.onUserMapClick(Object.values(foundFeatures), e.point.x, e.point.y);
    };

    private isVectorTilesSource(dataSource: DataSource): dataSource is DataSourceVectorTiles {
        return dataSource.dataLoadMethod === 'VectorTiles';
    }

    private toMapClickedFeature(feature: MapGeoJSONFeature): MapFeature {
        return {
            featurePropertiesData: feature.properties || {},
            featureStateData: feature.state || {},
            id: this.isVectorTilesSource(this.props.dataSources[feature.source])
                ? feature.id : feature.properties.id,
            layer: this.mapLayerToLayer[feature.layer.id],
            mapLayer: feature.layer.id as unknown as MAP_LAYER,
            source: feature.source,
        };
    }

    private readonly onUserMapRightClicked = (mapMouseEvent: MapMouseEvent) => {
        if (!this.mapInstance) {
            return;
        }

        const foundFeatures: { [key: string]: MapFeature } = {};
        const queriedFeatures = this.mapInstance.queryRenderedFeatures(mapMouseEvent.point, {
            layers: this.activeLayerIDs,
        });

        queriedFeatures.forEach((feature: MapGeoJSONFeature) => {
            if (feature.sourceLayer && !feature.state.color) {
                return;
            }

            const sourceFeature = (
                this.props.dataSources[feature.source].layerMapData as FeatureCollection
            ).features.find((ft) => ft.properties?.id === feature.properties?.id);

            foundFeatures[feature.properties.id] = {
                featurePropertiesData: feature.properties || {},
                featureStateData: feature.state || {},
                id: feature.properties.id,
                layer: this.mapLayerToLayer[feature.layer.id],
                mapLayer: feature.layer.id as unknown as MAP_LAYER,
                source: feature.source,
                geometry: sourceFeature?.geometry,
            };
        });

        this.props.onUserMapRightClick(Object.values(foundFeatures), mapMouseEvent.point.x, mapMouseEvent.point.y);
    };

    private readonly updateLayerSequence = () => {
        if (!this.mapInstance) {
            return;
        }

        const {mapLayers} = this.props;

        const mapLayersToSort: { [key: number]: string[] } = {};

        for (const mapLayersKey in mapLayers) {
            if (!mapLayers.hasOwnProperty(mapLayersKey)) {
                continue;
            }

            const item = mapLayers[mapLayersKey];

            const mapLayerIDs: string[] = [];

            for (const mapLayer of item.layers) {
                if (this.mapInstance.getLayer(mapLayer.id)) {
                    mapLayerIDs.push(mapLayer.id);
                }
            }

            if (item.isActive) {
                if (mapLayersToSort.hasOwnProperty(item.zIndex)) {
                    mapLayersToSort[item.zIndex] = [...mapLayersToSort[item.zIndex], ...mapLayerIDs];
                } else {
                    mapLayersToSort[item.zIndex] = mapLayerIDs;
                }
            }
        }

        let lastMapLayerID: string | undefined;

        for (const mapLayersToSortKey in mapLayersToSort) {
            if (!mapLayersToSort.hasOwnProperty(mapLayersToSortKey)) {
                continue;
            }

            const mapLayerIDs = mapLayersToSort[mapLayersToSortKey];

            for (const mapLayerID of mapLayerIDs) {
                // noinspection JSUnusedAssignment
                this.mapInstance.moveLayer(mapLayerID, lastMapLayerID);
                lastMapLayerID = mapLayerID;
            }
        }

        this.mapInstance.moveLayer('highlightLayerLine');
        this.mapInstance.moveLayer('highlightLayerSymbol');
    };

    private readonly mapFlyToLocation = (newLocation: MapFlyTo) => {
        if (!this.mapInstance) {
            return;
        }

        this.mapInstance.flyTo(newLocation);
    };

    private readonly setFilters = (filters: MapFilters, showTrafficJamIcons: boolean) => {
        if (!this.mapInstance) {
            return;
        }

        if (this.mapInstance.getLayer('wazeOtherTrafficAlerts')) {
            const wazeAlertsOtherFilter: FilterSpecification = ['all', this.mapLayerBaseFilters.wazeAlertsOther as LegacyFilterSpecification];
            if (filters.wazeAlertNdwKnown) {
                wazeAlertsOtherFilter.push(['==', 'ndw', false]);
            }
            this.mapInstance.setFilter('wazeOtherTrafficAlerts', wazeAlertsOtherFilter);
        }

        this.filterWazeAlertIncidentsLayer('wazeAlertsText', filters);
        this.filterWazeAlertIncidentsLayer('wazeAlertsSymbol', filters);
        this.filterDfineLayer(filters);
        this.filterPrefixes(filters);
        this.filterFdLayer(filters);
        this.filterSituationOtisLayer('trafficMessageOtis', filters, showTrafficJamIcons);
        this.filterSituationFlisvisLayer();
    };

    private readonly filterSituationOtisLayer = (layerNamePrefix: string, filters: MapFilters, showTrafficJamIcons: boolean) => {
        if (!this.mapInstance) {
            return;
        }

        this.mapInstance.getStyle().layers
            .filter((layer) => layer.id.startsWith(layerNamePrefix))
            .map((layer) => layer.id)
            .forEach((layerName) => {
                if (this.mapInstance?.getLayer(layerName)) {
                    const otisFilter: FilterSpecification = ['all', this.mapLayerBaseFilters[layerName] as LegacyFilterSpecification];

                    filters.situationTypeFilters.forEach((filter) => {
                        filter.value.split(';').forEach((value) => {
                            const filterSpecification: LegacyFilterSpecification = ['!=', 'type', value];
                            otisFilter.push(filterSpecification);
                        });
                    });

                    if (!showTrafficJamIcons && layerName === 'trafficMessageOtisPoint') {
                        otisFilter.push(['!=', 'type', 'AbnormalTraffic']);
                    }
                    this.mapInstance.setFilter(layerName, otisFilter);
                }
            });
    };

    private readonly filterSituationFlisvisLayer = () => {
        if (!this.mapInstance) {
            return;
        }
        if (this.mapInstance.getLayer('trafficMessageFlisvis')) {
            const layerFilter: LegacyFilterSpecification = [
                'all',
                this.mapLayerBaseFilters['trafficMessageFlisvis'] as LegacyFilterSpecification,
            ];

            this.mapInstance.setFilter('trafficMessageFlisvisLine', layerFilter);
            this.mapInstance.setFilter('trafficMessageFlisvis', layerFilter);
        }
    };

    private readonly setLineOffsetTravelTimeLayerIfZoomLevelReached = (lineOffset: number) => {
        if (!this.mapInstance) {
            return;
        }

        const layerIds = ['travelTimesFcdLine', 'travelTimesOther', 'traveltime-fcd-line', 'traveltime-other-line'];
        for (const layerId of layerIds) {
            if (this.mapInstance.getLayer(layerId)) {
                this.mapInstance.setPaintProperty(layerId, 'line-offset', lineOffset ? lineOffset / 2 : 0);
            }
        }
    };

    private readonly filterWazeAlertIncidentsLayer = (layerName: string, filters: MapFilters) => {
        if (!this.mapInstance) {
            return;
        }

        if (!this.mapInstance.getLayer(layerName)) {
            return;
        }

        const wazeAlertsFilter: LegacyFilterSpecification = ['all'];
        if (filters.wazeAlertStatusSet) {
            wazeAlertsFilter.push(['==', 'status', 'NOT_REVIEWED'] as LegacyFilterSpecification);
        }

        this.mapInstance.setFilter(layerName, wazeAlertsFilter);
    };

    private readonly filterDfineLayer = (filters: MapFilters) => {
        if (!this.mapInstance) {
            return;
        }

        if (this.mapInstance.getLayer('dfineSymbol')) {
            const dfineFilters: LegacyFilterSpecification = ['all'];

            if (filters.dFineOnlyRvmNetwork) {
                dfineFilters.push(['==', 'onRvmNetwork', true] as LegacyFilterSpecification);
            }

            this.mapInstance.setFilter('dfineSymbol', dfineFilters);
        }
    };

    private readonly filterFdLayer = (filters: MapFilters) => {
        if (!this.mapInstance) {
            return;
        }
        if (this.mapInstance.getLayer('fdLine')) {
            const fdFilter: LegacyFilterSpecification = ['all'];
            if (filters.fdVerifiedTrafficJams) {
                fdFilter.push(['==', 'isVerified', false]);
            }
            this.mapInstance.setFilter('fdLine', fdFilter);
            this.mapInstance.setFilter('fdHeadIcon', [...fdFilter, this.mapLayerBaseFilters['fdHeadIcon']] as LegacyFilterSpecification);
        }
    };

    private readonly filterPrefixes = ({prefixFilter}: MapFilters) => {
        if (!this.mapInstance) {
            return;
        }

        this.filterPrefixOnLayer(prefixFilter.avg);
        this.filterPrefixOnLayer(prefixFilter.travelTimeV2);
    };

    private filterPrefixOnLayer(prefixLayerFilter: PrefixLayerFilter){
        if(!this.mapInstance){
            return;
        }
        const prefixes = prefixLayerFilter.prefixes;
        for (const mapLayerId of prefixLayerFilter.mapLayerIDs) {
            if (this.mapInstance.getLayer(mapLayerId)) {
                if (prefixes.length === 0) {
                    this.mapInstance.setFilter(mapLayerId);
                    return;
                }
                const mapFilter: unknown[] = ['any'];

                prefixes.forEach(prefix => mapFilter.push(['in', prefix, ['get', 'externalId']]));
                this.mapInstance.setFilter(mapLayerId, mapFilter as FilterSpecification);
            }
        }
    }

    private readonly setHighlightSourceFeatures = (features: MapFeature[]) => {
        if (!this.mapInstance) {
            return;
        }

        const dataSource: GeoJSONSource = this.mapInstance.getSource('highlightSource') as GeoJSONSource;

        if (!dataSource) {
            return;
        }

        dataSource.setData({
            type: 'FeatureCollection',
            features: features.map<Feature>((feature: MapFeature) => ({
                type: 'Feature',
                geometry: feature.geometry || {
                    type: 'Point',
                    coordinates: [1, 1],
                },
                id: feature.id,
                properties: {},
            })),
        });
    };

    private readonly setupHighLightLayer = (): void => {
        if (!this.mapInstance) {
            return;
        }

        this.mapInstance.addSource('highlightSource', {
            type: 'geojson',
            data: {type: 'FeatureCollection', features: []},
        });

        this.mapInstance.addLayer({
            source: 'highlightSource',
            id: 'highlightLayerSymbol',
            type: 'symbol',
            layout: {
                'icon-image': 'ndw-other-25',
                'icon-allow-overlap': true,
                'icon-offset': [0, -5],
            },
        });
        this.mapInstance.addLayer({
            source: 'highlightSource',
            id: 'highlightLayerLine',
            type: 'line',
            layout: {
                'line-join': 'round',
                'line-cap': 'round',
            },
            paint: {
                'line-width': 2,
                'line-color': '#F7893C',
                'line-offset': 5,
            },
        });
    };

    private notifyNumberOfFeaturesVisibleOnMap() {
        if (!this.mapInstance) {
            return;
        }

        const amountOfVisibleFeatures = this.mapInstance.queryRenderedFeatures(undefined, {
            layers: this.activeLayerIDs,
        }).length;

        const shouldNotify = amountOfVisibleFeatures !== this.previousVisibleFeaturesCount;

        if (shouldNotify || (amountOfVisibleFeatures === 0 && this.previousVisibleFeaturesCount === -1)) {
            const notifyNumberOfFeatures = () => {
                if (!this.mapInstance) {
                    return;
                }
                const delayedAmountOfVisibleFeatures = this.mapInstance.queryRenderedFeatures(undefined, {
                    layers: this.activeLayerIDs,
                }).length;

                if (this.previousVisibleFeaturesCount !== delayedAmountOfVisibleFeatures) {
                    this.props.numberOfFeaturesVisibleOnMap(delayedAmountOfVisibleFeatures);
                    this.previousVisibleFeaturesCount = delayedAmountOfVisibleFeatures;
                }
            };

            if (shouldNotify) {
                notifyNumberOfFeatures();
            } else {
                setTimeout(notifyNumberOfFeatures, this.numberOfFeaturesCalculationDelayMs);
            }
        }
    }
}
